import { useState, useCallback } from 'react';
import { MessageCircle, X, Send } from 'lucide-react';

// Response patterns configuration
const RESPONSE_PATTERNS = [
  {
    keywords: ['admision', 'admisión', 'matricula', 'matrícula'],
    response: 'Para información sobre admisiones, visita nuestra página de Información de Admisión o llámanos al (787) 362-5354. ¡Estaremos encantados de ayudarte!'
  },
  {
    keywords: ['programa', 'tutoria', 'tutoría', 'ofrecimientos'],
    response: 'Ofrecemos apoyo académico K-12 y consultoría educativa. Visita la sección Ofrecimientos para más detalles.'
  },
  {
    keywords: ['contacto', 'teléfono', 'telefono', 'llamar'],
    response: 'Puedes contactarnos al (787) 362-5354 o enviarnos un email a info@riseandshinezone.com. También puedes escribirnos por WhatsApp.'
  },
  {
    keywords: ['horario', 'hora', 'abierto'],
    response: 'Nuestro horario de atención es de lunes a viernes. Para más información, contáctanos directamente.'
  },
  {
    keywords: ['ubicacion', 'ubicación', 'donde', 'direccion', 'dirección'],
    response: 'Estamos ubicados en Dorado, Puerto Rico. Visita nuestra página de Contacto para más detalles.'
  }
];

const DEFAULT_RESPONSE = 'Gracias por tu interés. Un miembro de nuestro equipo te contactará pronto. También puedes llamarnos al (787) 362-5354.';
const INITIAL_MESSAGE = { id: 'welcome', text: '¡Hola! ¿En qué podemos ayudarte hoy? Estamos aquí para orientarte.', sender: 'bot' };

// Helper function to find matching response
const findResponse = (input) => {
  const lowerInput = input.toLowerCase();
  const match = RESPONSE_PATTERNS.find(pattern => 
    pattern.keywords.some(keyword => lowerInput.includes(keyword))
  );
  return match ? match.response : DEFAULT_RESPONSE;
};

// WhatsApp Button Component
const WHATSAPP_NUMBER = '17873625354';
const WHATSAPP_PREFILL = 'Hola, me interesa conocer más sobre Rise & Shine Learning Zone.';

const WhatsAppButton = () => (
  <a
    href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_PREFILL)}`}
    target="_blank"
    rel="noopener noreferrer"
    className="whatsapp-float"
    data-testid="whatsapp-float-button"
    aria-label="WhatsApp"
  >
    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="white">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
    </svg>
  </a>
);

// Chatbot Toggle Button Component
const ChatbotToggleButton = ({ isOpen, onClick }) => (
  <button
    onClick={onClick}
    className="chatbot-float"
    data-testid="chatbot-float-button"
    aria-label="Asistente Virtual"
  >
    {isOpen ? <X className="w-6 h-6 text-white" /> : <MessageCircle className="w-6 h-6 text-white" />}
  </button>
);

// Message Item Component
const MessageItem = ({ message }) => (
  <div 
    key={message.id} 
    className={`chatbot-message ${message.sender}`} 
    data-testid={`chatbot-message-${message.sender}`}
  >
    {message.text}
  </div>
);

// Chatbot Window Component
const ChatbotWindow = ({ messages, inputValue, onInputChange, onSend }) => {
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') onSend();
  };

  return (
    <div className="chatbot-window" data-testid="chatbot-window">
      <div className="chatbot-header">
        <h3>Asistente Virtual</h3>
        <p className="text-xs text-stone-200 mt-1">¿Necesitas ayuda? Estamos aquí para orientarte.</p>
      </div>

      <div className="chatbot-messages">
        {messages.map((msg) => (
          <MessageItem key={msg.id} message={msg} />
        ))}
      </div>

      <div className="chatbot-input">
        <input
          type="text"
          value={inputValue}
          onChange={(e) => onInputChange(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Escribe tu pregunta..."
          className="flex-1 px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#43b6b8] text-sm"
          data-testid="chatbot-input"
        />
        <button
          onClick={onSend}
          className="bg-[#43b6b8] text-white p-2 rounded-lg hover:bg-[#359a9c] transition-colors"
          data-testid="chatbot-send-button"
        >
          <Send className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

// Main FloatingButtons Component
export const FloatingButtons = () => {
  const [showChatbot, setShowChatbot] = useState(false);
  const [messages, setMessages] = useState([INITIAL_MESSAGE]);
  const [inputValue, setInputValue] = useState('');

  const handleSend = useCallback(() => {
    if (!inputValue.trim()) return;

    const userMessageId = `user-${Date.now()}`;
    const botMessageId = `bot-${Date.now()}`;
    const response = findResponse(inputValue);

    setMessages(prev => [...prev, { id: userMessageId, text: inputValue, sender: 'user' }]);
    setInputValue('');

    setTimeout(() => {
      setMessages(prev => [...prev, { id: botMessageId, text: response, sender: 'bot' }]);
    }, 500);
  }, [inputValue]);

  const toggleChatbot = useCallback(() => {
    setShowChatbot(prev => !prev);
  }, []);

  return (
    <>
      <WhatsAppButton />
      <ChatbotToggleButton isOpen={showChatbot} onClick={toggleChatbot} />
      {showChatbot && (
        <ChatbotWindow
          messages={messages}
          inputValue={inputValue}
          onInputChange={setInputValue}
          onSend={handleSend}
        />
      )}
    </>
  );
};
